import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import LegacyBaseDialog from '../LegacyBaseDialog'
import { ActionButton, DialogFooter, IDialogProps, PrimaryButton, TextField } from '@fluentui/react'
import { Formik, useFormikContext } from 'formik'
import DocumentDropdown, { DocumentSelectionType } from '../../Dropdown/DocumentDropdown'
import BaseDropdown, { ComboBoxOption } from '../../../components/Dropdown/BaseDropdown'
import Form from '../../Form/Form'
import {
    DocumentContext,
    DocumentIconNameMap,
    DocumentOverviewItemDto,
    FolderType,
    IDefaultFolderDto,
    IDocumentStackItemDto,
    IFolderOverviewItemDto,
    IRootOverviewItemDto,
    isFolder,
} from '../../../config/documentConfig'
import { formatInputDocumentName, formatTemplateDocumentName } from '../../../helpers/regexHelper'
import { FilePartyDto } from '../../../config/filePartyConfig'
import useUser from '../../../hooks/useUser'
import { DocumentType } from '../../../config/documentConfig'
import { findFolderInTree } from '../../../helpers/documentHelper'
import { IFileDto } from '../../../config/fileConfig'
import { DocumentDto, DocumentOpenType } from '../../../context/GlobalContext'
import * as Yup from 'yup'
import { ErrorType, NormalizedError } from '../../../reducers/errorReducer'
import { useSettings } from '../../../hooks/useSettings'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { stealthClick } from '../../StealthLink'
import useAxios from '../../../hooks/useAxios'
import { QueueStatus } from '../../../hooks/useQueueStateHandler'
import { useTranslation } from 'react-i18next'

type Values = {
    template: string
    name: string
    pivotId?: string
    fileId?: string
    folderId?: string
    userId: string
    type: DocumentType
}

type Props = IDialogProps & {
    values: Values
    context: DocumentContext
    folders: IRootOverviewItemDto
    documentType: DocumentType
    selectedFolder?: IFolderOverviewItemDto
    // The variables we can use to replace in file names.
    variables?: { file?: IFileDto; party?: FilePartyDto }
    reset?: (stack?: IDocumentStackItemDto[]) => void
}

type FormContainerProps = Props & {
    globalErrors: NormalizedError[]
    setStack: (stack: IDocumentStackItemDto[]) => void
}

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        template: Yup.string().required(translate('file.pivot.document.create.template.required')),
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
        name: Yup.string()
            .required()
            .test('is-valid-name', translate('file.pivot.document.create.name.required'), value => !value.match(/^\./)),
    })
}

const getFileNameExistsError = (translate: (key: string) => string): NormalizedError => {
    return {
        id: ErrorType.Graph,
        type: ErrorType.Graph,
        message: translate('file.pivot.document.create.error.exists'),
    }
}

function FormContainer(props: FormContainerProps): ReactElement {
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<Values>()
    const { settings } = useSettings()
    const defaultFolders = settings?.folders
    const templates = settings?.documents

    const extension = useMemo<string>(() => DocumentIconNameMap.get(props.documentType), [props.documentType])
    const templateOptions = useMemo<ComboBoxOption<DocumentDto>[]>(
        () =>
            (templates || [])
                .filter(template => template.context === props.context)
                .filter(template => template.type === props.documentType)
                .filter(template => !template.role || template.role === props.variables?.party?.role)
                .map(template => ({ key: template.uri, text: template.label, data: template })),
        [templates, props.documentType, props.context, props.variables],
    )
    const folderData = useMemo<IFolderOverviewItemDto>(() => {
        if (!props.folders) {
            return undefined
        }

        // Filter folders that should not contain office documents.
        const exclude: string[] = (defaultFolders || [])
            .filter((folder: IDefaultFolderDto) => folder.excluded)
            .map((folder: IDefaultFolderDto) => folder.label.toLowerCase())

        return {
            ...props.folders,
            children: props.folders.children?.filter(
                (node: DocumentOverviewItemDto) => !exclude.find((search: string) => node.name.toLowerCase().indexOf(search) !== -1),
            ),
        }
    }, [defaultFolders, props.folders])

    useEffect(() => {
        // Take the first from the list if no template has been set yet.
        if (!values.template && templateOptions.length > 0) {
            setFieldValue('template', templateOptions[0].key)
        }
    }, [templateOptions])

    useEffect(() => {
        const template = (templates || []).find(template => template.uri === values.template)

        if (template) {
            setFieldValue('name', formatInputDocumentName(formatTemplateDocumentName(template.name, props.variables), extension))
        }

        const folderId =
            (template && findFolderInTree(props.folders, template?.folder || FolderType.Correspondence, defaultFolders)?.id) ||
            props.selectedFolder?.id ||
            ''

        setFieldValue('folderId', folderId)
        props.setStack(folderData?.children.find(folder => folder.id === folderId)?.stack)
    }, [templates, defaultFolders, values.template, props.selectedFolder])

    return (
        <Form globalErrors={props.globalErrors}>
            <BaseDropdown<DocumentDto>
                label={translate('file.pivot.document.create.template.label')}
                placeholder={translate('file.pivot.document.create.template.label')}
                required
                options={templateOptions}
                selectedKey={values.template}
                setSelectedKey={option => {
                    setFieldValue('template', option?.key)
                }}
            />
            <DocumentDropdown
                label={translate('file.pivot.document.create.folderid.label')}
                placeholder={translate('file.pivot.document.create.folderid.label')}
                required
                selectionType={DocumentSelectionType.FolderOnly}
                data={folderData}
                selectedKey={values.folderId}
                onChange={(_, option?: ComboBoxOption<IFolderOverviewItemDto>) => {
                    props.setStack(option.data.stack)
                    setFieldValue('folderId', option.key.toString())
                }}
            />
            <TextField
                label={translate('file.pivot.document.create.name.label')}
                placeholder={translate('file.pivot.document.create.name.label')}
                autoFocus
                required
                value={values.name?.replace(`.${extension}`, '') || ''}
                suffix={`.${extension}`}
                onChange={(_, value?: string) => setFieldValue('name', formatInputDocumentName(value, extension))}
            />
            <DialogFooter>
                <PrimaryButton text={translate('action.create.document')} type="submit" />
                <ActionButton text={translate('action.cancel')} onClick={props.onDismiss} />
            </DialogFooter>
        </Form>
    )
}

export default function OfficeDocumentDialog(props: Props): ReactElement {
    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const axios = useAxios()
    const { currentUser } = useUser()
    const { t: translate } = useTranslation()
    const { settings } = useSettings()

    const [stack, setStack] = useState<IDocumentStackItemDto[]>()
    const submitOfficeRequest = useSubmitDocumentGenerationRequest<Values, DocumentGenerationStatusData>(DocumentGenerationType.Office, {
        [QueueStatus.Queued]: () => props.onDismiss(),
        [QueueStatus.Finished]: data => {
            props.reset?.(stack)
            stealthClick(
                { id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                axios,
            )
        },
    })
    const submitOfficeV2Request = useSubmitDocumentGenerationRequest<Values, DocumentGenerationStatusData>(DocumentGenerationType.OfficeV2, {
        [QueueStatus.Queued]: () => props.onDismiss(),
        [QueueStatus.Finished]: data => {
            props.reset?.(stack)
            stealthClick(
                { id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                axios,
            )
        },
    })

    return (
        <LegacyBaseDialog dialogContentProps={{ title: 'Bestand aanmaken', subText: 'Selecteer de map en bestandsnaam:' }} {...props}>
            <Formik<Values>
                initialValues={{
                    template: '',
                    folderId: '',
                    name: '',
                    userId: currentUser?.id,
                    fileId: props.variables?.file?.id || '',
                    pivotId: props.variables?.party?.pivotId || '',
                    type: props.documentType,
                }}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={getValidationSchema(translate)}
                onSubmit={values => {
                    setGlobalErrors(undefined)
                    const folder: IFolderOverviewItemDto =
                        props.folders.children.find(parent => parent.id === stack[stack.length - 1].id) || props.folders
                    const childFolder = folder.children.find(folder => folder.id === values.folderId)
                    const template = settings.documents.find(template => template.uri === values.template)
                    if (isFolder(childFolder) && childFolder.children.some(child => child.name === values.name)) {
                        return setGlobalErrors([getFileNameExistsError(translate)])
                    }
                    if (template.version === 2) {
                        submitOfficeV2Request(values, template.label)
                    } else {
                        submitOfficeRequest(values, template.label)
                    }
                }}
            >
                <FormContainer globalErrors={globalErrors} setStack={setStack} {...props} />
            </Formik>
        </LegacyBaseDialog>
    )
}
